import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import { usePlanosPagamento } from '../../contexts/PlanosPagamentoContext';

// Corrigindo os caminhos dos imports com caminhos absolutos
import InformacoesBasicas from '../../pages/PlanosPagamento/Steps/InformacoesBasicas';
import ConfiguracaoPrestacoes from '../../pages/PlanosPagamento/Steps/ConfiguracaoPrestacoes';
import ConfiguracaoMetodosPagamento from '../../pages/PlanosPagamento/Steps/ConfiguracaoMetodosPagamento';
import Confirmacao from '../../pages/PlanosPagamento/Steps/Confirmacao';

const steps = [
  'Informações Básicas',
  'Configuração das Prestações',
  'Métodos de Pagamento',
  'Confirmação'
];

const EditarPlanoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { obterPlanoPagamento, atualizarPlanoPagamento } = usePlanosPagamento();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const carregarPlano = async () => {
      try {
        setLoading(true);
        const plano = await obterPlanoPagamento(id);

        if (plano.tipo === 'INDIVIDUAL' && !plano.planoBase) {
          setError('Erro: Plano individual sem referência ao plano base');
          return;
        }

        setFormData(plano);
      } catch (error) {
        console.error('Erro ao carregar plano:', error);
        setError('Erro ao carregar dados do plano');
      } finally {
        setLoading(false);
      }
    };

    carregarPlano();
  }, [id, obterPlanoPagamento]);

  const handleNext = () => {
    // Validar o passo atual antes de avançar
    if (validateStep(activeStep)) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleUpdateForm = (updates) => {
    setFormData(prev => {
      // Se for plano individual, não permitir alterações em certos campos
      if (prev.tipo === 'INDIVIDUAL') {
        const camposProtegidos = ['tipo', 'planoBaseId'];
        const updatesPermitidos = Object.keys(updates)
          .filter(key => !camposProtegidos.includes(key))
          .reduce((obj, key) => {
            obj[key] = updates[key];
            return obj;
          }, {});

        return { ...prev, ...updatesPermitidos };
      }
      return { ...prev, ...updates };
    });
  };

  const validateStep = (step) => {
    switch(step) {
      case 0: // Informações Básicas
        if (!formData.descricao || !formData.anoLetivo) {
          setError('Preencha todos os campos obrigatórios');
          return false;
        }
        break;
      case 1: // Prestações
        if (!formData.prestacoes?.length) {
          setError('Adicione pelo menos uma prestação');
          return false;
        }
        break;
      case 2: // Métodos de Pagamento
        if (formData.tipo === 'BASE' && !formData.metodosDisponiveisJSON) {
          setError('Selecione pelo menos um método de pagamento');
          return false;
        }
        break;
      default:
        break;
    }
    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    try {
      if (!validateStep(activeStep)) return;

      // Se for plano individual, manter referência ao plano base
      const dadosAtualizacao = formData.tipo === 'INDIVIDUAL' 
        ? {
            ...formData,
            planoBaseId: formData.planoBase.id
          }
        : formData;

      await atualizarPlanoPagamento(id, dadosAtualizacao);
      navigate('/planos-pagamento');
    } catch (error) {
      console.error('Erro ao atualizar plano:', error);
      setError('Erro ao salvar alterações');
    }
  };

  const renderStepContent = (step) => {
    if (!formData) return null;

    const commonProps = {
      formData,
      onUpdateForm: handleUpdateForm,
      error,
      tipo: formData.tipo
    };

    switch (step) {
      case 0:
        return (
          <>
            {formData.tipo === 'INDIVIDUAL' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Este é um plano individual baseado em: {formData.planoBase?.descricao}
              </Alert>
            )}
            <InformacoesBasicas {...commonProps} />
          </>
        );
      case 1:
        return <ConfiguracaoPrestacoes {...commonProps} />;
      case 2:
        return <ConfiguracaoMetodosPagamento {...commonProps} />;
      case 3:
        return <Confirmacao formData={formData} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !formData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Editar {formData?.tipo === 'BASE' ? 'Plano Base' : 'Plano Individual'}
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent>
          {error && activeStep !== 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {renderStepContent(activeStep)}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            {activeStep > 0 && (
              <Button onClick={handleBack} sx={{ mr: 1 }}>
                Voltar
              </Button>
            )}
            {activeStep < steps.length - 1 ? (
              <Button 
                variant="contained" 
                onClick={handleNext}
                disabled={loading}
              >
                Próximo
              </Button>
            ) : (
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSubmit}
                disabled={loading}
              >
                Salvar Alterações
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EditarPlanoPage;